import { Calendar, Clock, FileText, Lock, Mail, MapPin, Paperclip, Phone, Send } from "lucide-react";
import { formatAmount } from "@/utils/formatAmount";
import { Badge } from "@/components/ui/Badge";
import { ManagedFileLink } from "@/components/ui/ManagedFileLink";
import type { PublicProceeding } from "@/features/rfq/api/proceedings";
import { OfferPanel } from "@/features/rfq/components/OfferPanel";
import { CommunicationsPanel } from "@/features/rfq/components/CommunicationsPanel";
import { QuestionsOfferer } from "@/features/rfq/components/QuestionsOfferer";
import { offererDeadlineTone } from "@/features/rfq/components/ProceedingOffererCard";
import { Countdown } from "@/features/rfq/components/Countdown";
import { ProceedingMdcpScopeMap } from "@/features/rfq/components/ProceedingMdcpScopeMap";

/**
 * Pełna strona postępowania oczami oferenta.
 *
 * To jest CAŁA strona, którą widzi podwykonawca — nie kafelek. Ten sam komponent
 * renderuje realną podstronę oferenta (/contractor/proceedings/:id) oraz modal
 * „Widok oferenta" po stronie GK; dzięki temu podgląd pokazuje dokładnie to, co
 * firma, a nie nasze wyobrażenie o tym.
 *
 * `preview` wyłącza akcje (przyciski składania oferty) — GK ogląda, nie licytuje.
 */

interface Props {
  proceeding: PublicProceeding;
  preview?: boolean;
}

export function ProceedingOffererDetail({ proceeding, preview = false }: Props) {
  const status = offererDeadlineTone(proceeding.submission_deadline, proceeding.is_open_for_offers);

  return (
    <div className="max-w-3xl">
      {/* Nagłówek — jak każda strona szczegółów w systemie */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs text-[var(--color-text-muted)]">
            {proceeding.number}
          </span>
          <Badge color={status.color}>{status.label}</Badge>
          {proceeding.runoff?.is_open && <Badge color="orange">Dopytanie</Badge>}
        </div>
        <h1 className="mt-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
          {proceeding.title}
        </h1>
        <div className="mt-1 h-0.5 w-8 bg-[var(--color-primary)]" />
        {/* Po terminie pierwszej tury liczy się już tylko zegar dogrywki. */}
        {proceeding.is_open_for_offers ? (
          <div className="mt-3">
            <Countdown deadline={proceeding.submission_deadline} />
          </div>
        ) : (
          proceeding.runoff?.is_open && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-sm text-[var(--color-text-secondary)]">
                Do końca dopytania:
              </span>
              <Countdown deadline={proceeding.runoff.deadline} />
            </div>
          )
        )}
      </div>

      {/* Warunki — pierwsza rzecz, której szuka oferent */}
      <section className="mb-6 grid gap-px bg-[var(--color-border)] sm:grid-cols-2 lg:grid-cols-4">
        <Tile icon={<Clock size={12} />} label="Termin składania ofert">
          {new Date(proceeding.submission_deadline).toLocaleString("pl", {
            dateStyle: "short",
            timeStyle: "short",
          })}
        </Tile>
        <Tile icon={<Calendar size={12} />} label="Termin realizacji">
          {proceeding.delivery_deadline
            ? new Date(proceeding.delivery_deadline).toLocaleDateString("pl")
            : "—"}
        </Tile>
        <Tile icon={<MapPin size={12} />} label="Województwo">
          {proceeding.voivodeship_label || "bez znaczenia"}
          {proceeding.county_labels.length > 0 && (
            <span className="block text-xs text-[var(--color-text-muted)]">
              powiaty: {proceeding.county_labels.join(", ")}
            </span>
          )}
        </Tile>
        <Tile label="Cena maksymalna">
          {proceeding.max_price_net ? (
            `${formatAmount(proceeding.max_price_net)} zł netto`
          ) : (
            <span className="inline-flex items-center gap-1 text-[var(--color-text-muted)]">
              <Lock size={11} /> nie podano
            </span>
          )}
          {proceeding.price_basis && (
            <span className="mt-0.5 block text-xs font-normal text-[var(--color-text-secondary)]">
              Cena {proceeding.price_basis}
            </span>
          )}
        </Tile>
      </section>

      {/* Opis */}
      <section className="mb-6">
        <SectionTitle>Opis zamówienia</SectionTitle>
        <div className="bg-white p-4 dark:bg-gray-800">
          {proceeding.description ? (
            <p className="whitespace-pre-wrap text-sm text-[var(--color-text-secondary)]">
              {proceeding.description}
            </p>
          ) : (
            <p className="text-sm text-[var(--color-text-muted)]">Brak dodatkowego opisu.</p>
          )}
        </div>
      </section>

      {/* Zakres prac */}
      {proceeding.positions_detail.length > 0 && (
        <section className="mb-6">
          <SectionTitle>Zakres prac</SectionTitle>
          <div className="bg-white p-4 dark:bg-gray-800">
            <ul className="space-y-1 text-sm">
              {proceeding.positions_detail.map((p) => (
                <li key={p.id} className="flex items-start gap-2">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[var(--color-text-muted)]" />
                  <span className="text-[var(--color-text-primary)]">
                    {p.name}
                    <span className="ml-1 text-xs text-[var(--color-text-muted)]">
                      ({p.category_name})
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Załączniki */}
      <section className="mb-6">
        <SectionTitle>
          <Paperclip size={14} className="mr-1 inline" /> Załączniki ({proceeding.attachments.length})
        </SectionTitle>
        <div className="bg-white p-4 dark:bg-gray-800">
          {proceeding.attachments.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)]">
              Do tego postępowania nie dołączono dokumentów.
            </p>
          ) : (
            <ul className="space-y-2">
              {proceeding.attachments.map((attachment) => (
                <li
                  key={attachment.id}
                  className="flex flex-wrap items-center gap-2 border-b border-[var(--color-border)] pb-2 last:border-0 last:pb-0"
                >
                  <FileText size={14} className="text-[var(--color-text-muted)]" />
                  <span className="text-sm text-[var(--color-text-primary)]">
                    {attachment.label || attachment.original_filename}
                  </span>
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {attachment.original_filename}
                  </span>
                  <span className="text-xs text-[var(--color-text-muted)]">
                    · dodano{" "}
                    {new Date(attachment.created_at).toLocaleDateString("pl")}
                  </span>
                  <span className="ml-auto">
                    <ManagedFileLink file={attachment}>Pobierz</ManagedFileLink>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Zakres MDCP na mapie (OSM) — tylko postępowania na mapę do celów projektowych.
          Punkty i obrys są zamrożone od publikacji, więc oferent widzi dokładnie to,
          co po wygranej trafi do jego zlecenia. */}
      {proceeding.mdcp_scope && <ProceedingMdcpScopeMap scope={proceeding.mdcp_scope} />}

      {/* Kupujący — karta kontaktowa identyczna jak „Zamawiający" przy zamówieniu.
          Telefon/mail idą z profilu osoby, więc zmiana kupującego aktualizuje je na żywo. */}
      <section className="mb-6">
        <SectionTitle>Kupujący</SectionTitle>
        <div className="border-l-4 border-[var(--color-primary)] bg-white p-4 dark:bg-gray-800">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
            {proceeding.responsible_name ?? "—"}
          </p>
          <div className="mt-2 flex flex-col gap-1 text-sm">
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <Phone size={13} className="shrink-0" />
              {proceeding.responsible_phone ? (
                <a href={`tel:${proceeding.responsible_phone}`} className="break-all hover:underline">
                  {proceeding.responsible_phone}
                </a>
              ) : (
                <span className="text-gray-400 dark:text-gray-500">brak numeru</span>
              )}
            </span>
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <Mail size={13} className="shrink-0" />
              {proceeding.responsible_email ? (
                <a
                  href={`mailto:${proceeding.responsible_email}`}
                  className="break-all hover:underline"
                >
                  {proceeding.responsible_email}
                </a>
              ) : (
                <span className="text-gray-400 dark:text-gray-500">brak adresu</span>
              )}
            </span>
          </div>
        </div>
      </section>

      {/* Oferta */}
      <section>
        <SectionTitle>Twoja oferta</SectionTitle>
        {preview ? (
          <div className="border-2 border-dashed border-[var(--color-border)] bg-white p-6 text-center dark:bg-gray-800">
            <Send size={22} className="mx-auto mb-2 text-[var(--color-text-muted)]" />
            {/* `is_open_for_offers` jest fałszywe z DWÓCH różnych powodów: termin minął
                albo ogłoszenie jeszcze nie zostało opublikowane. Wrzucenie obu do jednego
                zdania kazało podglądowi szkicu pisać „po terminie" pod terminem, który
                dopiero nadejdzie. */}
            <p className="text-sm text-[var(--color-text-secondary)]">
              W widoku firmy tu jest formularz składania oferty
              {proceeding.is_open_for_offers
                ? "."
                : proceeding.status === "draft"
                  ? " (szkic — otworzy się po publikacji)."
                  : " (obecnie po terminie)."}
            </p>
            <p className="mt-1 text-xs text-[var(--color-text-muted)]">
              Podgląd GK — kwot ofert nie składa się z tego miejsca.
            </p>
          </div>
        ) : (
          <OfferPanel proceeding={proceeding} />
        )}
      </section>

      {!preview && (
        <>
          <section className="mt-6">
            <SectionTitle>Komunikaty</SectionTitle>
            <CommunicationsPanel proceedingId={proceeding.id} />
          </section>
          <section className="mt-6">
            <SectionTitle>Pytania do postępowania</SectionTitle>
            {/* Pytania są otwarte także w dogrywce — kupiec potrafi dopytać firmę
                o jej ofertę, więc musi ona mieć czym odpisać. */}
            <QuestionsOfferer
              proceedingId={proceeding.id}
              open={
                proceeding.is_open_for_offers || !!proceeding.runoff?.is_open
              }
            />
          </section>
        </>
      )}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-2 border-l-2 border-[var(--color-primary)] pl-2 text-base font-semibold text-[var(--color-text-primary)]">
      {children}
    </h2>
  );
}

function Tile({
  icon,
  label,
  children,
}: {
  icon?: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white p-3 dark:bg-gray-800">
      <p className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
        {icon}
        {label}
      </p>
      <p className="mt-0.5 text-sm text-[var(--color-text-primary)]">{children}</p>
    </div>
  );
}
